/**
 * The avatar for a person in the appellants registry (§14.2.6).
 *
 * ===========================================================================
 * IT IS A MONOGRAM, AND IT IS NEVER A PHOTO.
 * ===========================================================================
 *
 * D-044 rules out face recognition and there is no citizen photograph anywhere
 * in this platform — so a circular slot that LOOKS like an avatar must not be
 * one. A silhouette placeholder is the shape that invites "why is the photo
 * missing?", and the honest answer ("there was never a photo") is the one a
 * placeholder is worst at giving.
 *
 * For an appellant with no `display_name` — someone whose every appeal was a
 * recording, whose name is spoken inside it and is deliberately not transcribed
 * into a registry field — the monogram is an em dash. Not an initial guessed
 * from somewhere else, not a `?`, not a generic person glyph: a dash, which is
 * this panel's established rendering of "no data" (`formatMetric`).
 */
export function AppellantMonogram({
  name,
  large = false,
}: {
  name: string | undefined;
  large?: boolean;
}): React.JSX.Element {
  // The first character of the first word. Deliberately ONE character: a
  // two-letter monogram built from "family name + given name" gets the order
  // wrong for Uzbek names about half the time, and a wrong initial reads as a
  // different person.
  const initial = name?.trim().charAt(0).toUpperCase();
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-chip bg-brand-50 font-semibold text-brand-800 ${
        large ? 'h-12 w-12 text-lg' : 'h-7 w-7 text-xs'
      }`}
    >
      {initial && initial.length > 0 ? initial : '—'}
    </span>
  );
}
